<script lang="ts">
  import { onDestroy } from 'svelte';
  import { Link, navigate } from 'svelte-routing';
  import { api } from '../../lib/api/client';
  import type { Activity, MemberLocation } from '../../lib/api/types';
  import { auth, currentUser, isAuthenticated } from '../../lib/stores/auth';
  import { confirm } from '../../lib/stores/confirmDialog';
  import Avatar from '../../lib/components/Avatar.svelte';
  import RouteMap from '../../lib/components/activities/RouteMap.svelte';
  import Companions from '../../lib/components/activities/Companions.svelte';
  import ActivityCharts from '../../lib/components/activities/ActivityCharts.svelte';
  import { activityIcon, activityLabel, climb, clockTime, length, rate, usesPace } from '../../lib/utils/activities';
  import { usesMiles } from '../../lib/stores/units.svelte';
  import { SPORTS } from '../../lib/utils/sports';
  import { when } from '../../lib/utils/time';
  import { effortBadge, type Series, type SeriesPoint } from '../../lib/utils/training';
  import { t } from '../../lib/i18n/index.ts';

  // ActivityDetail is one activity (spec 006 FR-037, 006 FR-038, 006 FR-039, 006 FR-040): its route
  // with the start and finish above a panel with who, when, where, its name,
  // the stats it has, its splits, and the device it was recorded on. A
  // timer-only activity has no map. The owner can rename it, change its
  // sport, export it, and delete it. Without groupId it is the viewer's own.
  // A finished one charts its pace, heart rate, and elevation with a marker
  // on the map where the charts are scrubbed to (spec 006 FR-041, 006 FR-042, 006 FR-043), and says which of its best efforts rank (006 FR-086).
  let { activityId, groupId }: { activityId: string; groupId?: string } = $props();
  // miles is whether distance and speed show in miles (010 FR-034).
  let miles = $derived(usesMiles());

  let a = $state<Activity | null>(null);
  let who = $state<{ name: string; userId: string; color: string; photo?: string } | null>(null);
  let error = $state('');
  let series = $state<Series | null>(null);
  let marker = $state<SeriesPoint | null>(null);
  let poll: ReturnType<typeof setTimeout> | undefined;
  let own = $derived(!groupId);
  let back = $derived(own ? '/activities' : a ? `/activities/${groupId}/${a.user_id}` : '/');

  $effect(() => {
    if (!$isAuthenticated) navigate('/login', { replace: true });
  });

  $effect(() => {
    void activityId;
    void groupId;
    a = null;
    series = null;
    marker = null;
    load();
  });

  onDestroy(() => clearTimeout(poll));

  // load fetches the activity, and again every 10 seconds while it records.
  async function load() {
    clearTimeout(poll);
    try {
      a = await api.get<Activity>(own ? `/me/activities/${activityId}?route=true` : `/groups/${groupId}/activities/${activityId}?route=true`);
      error = '';
      if (a.state !== 'finished') poll = setTimeout(load, 10_000);
      if (!who) loadWho(a.user_id);
      if (a.state === 'finished' && !series) loadSeries();
    } catch (e: any) {
      error = e.message;
    }
  }

  async function loadSeries() {
    try {
      series = await api.get<Series>(own ? `/me/activities/${activityId}/series` : `/groups/${groupId}/activities/${activityId}/series`);
    } catch {
      // The stats and splits read well enough without charts.
    }
  }

  async function loadWho(userId: string) {
    if (own) {
      const u = $auth.user;
      if (u) who = { name: u.display_name, userId: u.id, color: u.color ?? 'var(--theme-overlay2)', photo: u.photo };
      return;
    }
    try {
      const m = (await api.get<MemberLocation[]>(`/groups/${groupId}/locations`)).find((x) => x.user_id === userId);
      if (m) who = { name: m.display_name, userId, color: m.color, photo: m.photo };
    } catch {
      // The panel reads well enough without the member's name.
    }
  }

  async function rename() {
    if (!a) return;
    const name = prompt(t('activitiesRenamePrompt'), a.name)?.trim();
    if (!name || name === a.name) return;
    await edit({ name });
  }

  async function changeSport(e: Event) {
    const type = (e.currentTarget as HTMLSelectElement).value;
    if (!a || type === a.type) return;
    await edit({ type });
  }

  async function edit(body: { name?: string; type?: string }) {
    if (!a) return;
    try {
      const got = await api.patch<Activity>(`/me/activities/${a.id}`, body);
      a = { ...a, name: got.name, type: got.type, gps: got.gps, load: got.load, load_estimated: got.load_estimated, efforts: got.efforts };
      // A sport changes pace to speed and which efforts count.
      if (body.type) loadSeries();
      error = '';
    } catch (e: any) {
      error = e.message;
    }
  }

  async function exportGPX() {
    if (!a) return;
    try {
      await api.download(`/me/activities/${a.id}/gpx`, `${a.name}.gpx`);
    } catch (e: any) {
      error = e.message;
    }
  }

  async function remove() {
    if (!a) return;
    if (!(await confirm(t('activitiesDeleteConfirm', { name: a.name })))) return;
    try {
      await api.delete(`/me/activities/${a.id}`);
      navigate('/activities');
    } catch (e: any) {
      error = e.message;
    }
  }

  // MEDAL_COLORS are gold, silver, and bronze from the theme.
  const MEDAL_COLORS = ['var(--theme-yellow)', 'var(--theme-overlay2)', 'var(--theme-peach)'];

  // sports are those the activity may change to: any sport for one with a
  // route, whose route stays stored when it becomes timer-only, and only
  // timer-only sports for one without.
  let sports = $derived(a ? SPORTS.filter((sp) => a!.gps || !sp.gps) : []);

  // stats are the ones the activity has; one it lacks is left out, never
  // shown as zero (006 FR-054).
  let stats = $derived.by(() => {
    if (!a) return [];
    const out: { label: string; value: string }[] = [];
    // A distance from steps says so wherever it shows (spec 006 FR-010).
    if (a.gps) out.push({ label: a.distance_estimated ? t('activitiesStatDistanceFromSteps') : t('activitiesStatDistance'), value: length(a.distance_m) });
    if (a.gps && a.timed) {
      const r = rate(a.type, a.distance_m, a.moving_s);
      if (r) out.push({ label: usesPace(a.type) ? t('activitiesStatAvgPace') : t('activitiesStatAvgSpeed'), value: r });
    }
    if (a.timed) {
      out.push({ label: a.gps ? t('activitiesStatMovingTime') : t('activitiesStatTime'), value: clockTime(a.moving_s) });
      if (a.gps && a.elapsed_s > a.moving_s) out.push({ label: t('activitiesStatElapsedTime'), value: clockTime(a.elapsed_s) });
    }
    // A workout imported without a route has no climb to show (spec 006 FR-117).
    if (a.gps && !a.no_route) out.push({ label: t('activitiesStatElevationGain'), value: climb(a.elevation_gain_m) });
    if (a.steps != null) {
      out.push({ label: t('activitiesStatSteps'), value: String(a.steps) });
      if (a.moving_s > 0) out.push({ label: t('activitiesStatAvgCadence'), value: t('activitiesSpm', { n: Math.floor((a.steps * 60) / a.moving_s) }) });
    }
    // Calories are an estimate from the sport, time, weight, and heart rate.
    if (a.calories != null) out.push({ label: t('activitiesStatCalories'), value: t('activitiesKcal', { n: a.calories }) });
    if (a.hr_avg != null) out.push({ label: t('activitiesStatAvgHeartRate'), value: t('activitiesBpm', { n: a.hr_avg }) });
    if (a.hr_max != null) out.push({ label: t('activitiesStatMaxHeartRate'), value: t('activitiesBpm', { n: a.hr_max }) });
    if (a.active_minutes != null) out.push({ label: t('activitiesStatActiveMinutes'), value: t('activitiesMinutes', { n: a.active_minutes }) });
    // Load without heart rate is estimated from moving time and the sport (006 FR-089).
    if (a.load != null) out.push({ label: a.load_estimated ? t('activitiesStatLoadEstimated') : t('activitiesStatLoad'), value: String(Math.round(a.load)) });
    return out;
  });

  let splits = $derived(a ? (miles ? a.splits_mi : a.splits_km) : []);
  // hrSplits is each split's average heart rate, when the activity has any.
  let hrSplits = $derived(a ? ((miles ? a.hr_splits_mi : a.hr_splits_km) ?? []) : []);
  // wholeUnits is how many whole miles or kilometers the splits start with.
  let wholeUnits = $derived(a ? Math.floor(a.distance_m / (miles ? 1609.344 : 1000)) : 0);
  // An automatic walk was nobody's to start (spec 006 FR-107).
  let recordedOn = $derived(
    !a
      ? ''
      : a.source === 'imported'
        ? t('activitiesImported')
        : a.source === 'automatic'
          ? a.device_name
            ? t('activitiesRecordedAutomaticallyOn', { device: a.device_name })
            : t('activitiesRecordedAutomatically')
          : a.device_name
            ? t('activitiesRecordedOn', { device: a.device_name })
            : '',
  );
</script>

<div class="flex-1 flex flex-col">
  {#if a?.gps && !a.no_route}
    <RouteMap segments={a.route ?? []} ends finished={a.state === 'finished'} height="h-[45vh] min-h-64" marker={marker} />
  {/if}
  <div class="p-6 max-w-3xl w-full mx-auto space-y-4">
    <Link to={back} class="text-sm hover:opacity-80"><i aria-hidden="true" class="las la-arrow-left mr-1"></i>{own ? t('activitiesMine') : t('activitiesAll')}</Link>
    {#if error}
      <p class="text-sm" style="color: var(--theme-error);">{error}</p>
    {/if}
    {#if a}
      <div class="flex items-center gap-3">
        {#if who}
          <Avatar name={who.name} userId={who.userId} color={who.color} photo={who.photo} size={44} />
        {/if}
        <div class="min-w-0">
          {#if who}<p class="font-medium">{who.name}</p>{/if}
          <p class="text-sm text-text-muted">{a.start_place ? t('activitiesWhenAt', { when: when(a.started_at), place: a.start_place }) : when(a.started_at)}</p>
        </div>
      </div>

      <div class="flex flex-wrap items-center justify-between gap-2">
        <div>
          <h1 class="text-xl font-semibold">{a.name}</h1>
          <p class="text-sm text-text-muted"><i aria-hidden="true" class="las {activityIcon(a.type)} mr-1"></i>{activityLabel(a.type)}</p>
        </div>
        {#if own}
          <div class="flex flex-wrap gap-2">
            {#if a.state === 'finished'}
              <select class="input py-1 text-sm" value={a.type} onchange={changeSport} aria-label={t('activitiesSport')}>
                {#each sports as sp (sp.id)}
                  <option value={sp.id}>{sp.name}</option>
                {/each}
              </select>
            {/if}
            <button class="btn btn-secondary btn-sm" onclick={rename}>{t('activitiesRename')}</button>
            {#if a.gps && !a.no_route}
              <!-- A timer-only activity has no route to put in a file, nor has a workout imported without one. -->
              <button class="btn btn-secondary btn-sm" onclick={exportGPX}>{t('activitiesExportGpx')}</button>
            {/if}
            {#if a.state === 'finished'}
              <!-- The phone recording it would bring back one under way. -->
              <button class="btn btn-secondary btn-sm" onclick={remove}>{t('appDelete')}</button>
            {/if}
          </div>
        {/if}
      </div>

      {#if a.state !== 'finished'}
        <p class="text-sm" style="color: var(--theme-accent);">{a.state === 'paused' ? t('activitiesPausedNow') : t('activitiesRecordingNow')}</p>
      {/if}

      {#if a.efforts?.length}
        <ul class="flex flex-wrap gap-2" aria-label={t('activitiesBestEfforts')}>
          {#each [...a.efforts].sort((x, y) => x.rank - y.rank) as e (e.distance)}
            <li class="inline-flex items-center gap-1 rounded-full px-2.5 py-0.5 text-sm" style="background: var(--theme-surface0);">
              <i aria-hidden="true" class="las la-medal" style="color: {MEDAL_COLORS[e.rank - 1]};"></i>{effortBadge(e)}<span class="text-text-muted">{clockTime(e.seconds)}</span>
            </li>
          {/each}
        </ul>
      {/if}

      <dl class="grid grid-cols-2 sm:grid-cols-3 gap-4">
        {#each stats as s (s.label)}
          <div>
            <dt class="text-xs text-text-muted">{s.label}</dt>
            <dd class="text-lg font-semibold">{s.value}</dd>
          </div>
        {/each}
      </dl>
      {#if own && a.state === 'finished' && a.calories == null && !$currentUser?.weight_kg}
        <p class="text-sm mt-2"><Link to="/settings/activities" class="text-accent">{t('activitiesAddWeight')}</Link></p>
      {/if}

      {#if series}
        <ActivityCharts {series} zones={a.zones} onscrub={(p) => (marker = p)} />
      {/if}

      <Companions activity={a} {own} onchange={(got) => (a = got)} />

      {#if a.gps && a.timed && splits.length > 0}
        <table class="text-sm">
          <caption class="text-left font-medium mb-1">{t('activitiesSplits')}</caption>
          <thead><tr><th class="text-left pr-6 text-text-muted font-normal">{miles ? t('activitiesSplitMile') : t('activitiesSplitKilometer')}</th><th class="text-left pr-6 text-text-muted font-normal">{t('activitiesStatMovingTime')}</th>{#if hrSplits.length}<th class="text-left text-text-muted font-normal">{t('activitiesHeartRate')}</th>{/if}</tr></thead>
          <tbody>
            {#each splits as s, i (i)}
              <tr><td class="pr-6">{i < wholeUnits ? i + 1 : t('activitiesSplitLastPart')}</td><td class="pr-6">{clockTime(s)}</td>{#if hrSplits.length}<td>{hrSplits[i] != null ? t('activitiesBpm', { n: hrSplits[i] }) : '–'}</td>{/if}</tr>
            {/each}
          </tbody>
        </table>
      {/if}

      {#if recordedOn}
        <p class="text-sm text-text-muted"><i aria-hidden="true" class="las {a.source === 'imported' ? 'la-file-import' : 'la-mobile'} mr-1"></i>{recordedOn}</p>
      {/if}
    {/if}
  </div>
</div>
